import { QIcon_caf_overview, QIcon_chevron_right } from '@repo/icons/index'
import classNames from 'classnames'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { COLORS } from '../../../../modules/vars'
import HighlightedTitle from '../common/HighlightedTitle'

interface HighlightViewCardProps {
  isActive: boolean
  upperTitle: { text: string; icon: React.FC<React.SVGProps<SVGSVGElement>> }
  mainText: string
  onClick: () => void
  isSelected?: boolean
}

const HighlightViewCard = ({
  isActive,
  upperTitle = { text: '', icon: (props) => <QIcon_caf_overview {...props} color={COLORS.PURPLE500} /> },
  mainText = '',
  onClick,
  isSelected = false,
}: HighlightViewCardProps) => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div className={styles.container(isActive)} onClick={onClick}>
        <div className="flex justify-between">
          <div>
            <h5>{<LinkTitle content={upperTitle} />}</h5>
            <div className="mb-[4px] flex items-start justify-between">
              <h3 className="text-h5 text-black">
                {<HighlightedTitle title={mainText} color={isActive ? COLORS.PURPLE50 : COLORS.GRAY200} />}
              </h3>
            </div>
          </div>
          <QIcon_chevron_right width={12} height={12} color={COLORS.GRAY600} />
        </div>
      </div>
    )
  } else {
    return (
      <div className={styles.container_pc(isActive, isSelected)} onClick={onClick}>
        <div className="flex justify-between">
          <div>
            <h5>{<LinkTitle content={upperTitle} isActive={isActive} />}</h5>
            <div className="mb-[4px] flex items-start justify-between">
              <h3
                className={classNames('text-h5', {
                  'text-black': isActive,
                  'text-gray-500': !isActive,
                })}
              >
                {
                  <HighlightedTitle
                    disabled={!isActive}
                    title={mainText}
                    color={isActive ? COLORS.PURPLE50 : COLORS.GRAY50}
                  />
                }
              </h3>
            </div>
          </div>
          <QIcon_chevron_right width={12} height={12} color={COLORS.GRAY600} />
        </div>
      </div>
    )
  }
}

const LinkTitle = ({
  content,
  isActive = true,
}: {
  content: { text: string; icon: React.FC<React.SVGProps<SVGSVGElement>> }
  isActive?: boolean
}) => {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <div className="mb-4 flex items-center">
        <content.icon width={18} height={18} className="mr-[2px]" color={COLORS.PURPLE500} />
        <span
          className={classNames('text-h5 h-[18px] font-medium', {
            'text-purple-500': isActive,
            'text-gray-500': !isActive,
          })}
        >
          {content.text}
        </span>
      </div>
    )
  } else {
    return (
      <div
        className={classNames('mb-4 flex items-center', {
          'color-purple-500': isActive,
          'text-gray-500': !isActive,
        })}
      >
        <content.icon
          width={18}
          height={18}
          className="mr-[2px] [&>*]:fill-current"
          color={isActive ? COLORS.PURPLE500 : COLORS.GRAY500}
          fill={isActive ? COLORS.PURPLE500 : COLORS.GRAY500}
        />
        <span
          className={classNames('text-h5 h-[18px] font-medium', {
            'text-purple-500': isActive,
            'text-gray-500': !isActive,
          })}
        >
          {content.text}
        </span>
      </div>
    )
  }
}

export default HighlightViewCard

const styles = {
  container: (active: boolean) =>
    classNames(`mb-[8px] rounded-[6px] border border-gray-100 p-[16px] shadow-sm`, active ? `bg-white` : `bg-gray-200`),
  container_pc: (active: boolean, isSelected: boolean) =>
    classNames(
      `mb-[8px] w-[424px] cursor-pointer rounded-[6px] p-[16px] shadow-sm`,
      active ? (isSelected ? `bg-purple-50` : `bg-white`) : `bg-gray-100`,
      isSelected ? `border border-purple-500` : active ? `border border-gray-200` : `border border-gray-200`
    ),
}
